// =============================================================================
// PROJECTOR PAGE — Minimal slideshow + video display (no chat)
// Socket connection, slideshow sync from host owner, fullscreen toggle
// =============================================================================

(function() {
  'use strict';

  // Add template class for CSS scoping
  document.body.classList.add('projector-template');

  // Slideshow visibility (always on for projector)
  window.isSlideshowVisible = true;
  var currentImages = [];
  var currentSlideIndex = 0;
  var currentProductHandle = null;

  // ---- Socket.IO connection ----
  var config = window.MHV_LIVE_CONFIG || {};
  var customer = config.customer || {};

  var socket = io(MHV.SERVER_URL, {
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    transports: ['websocket', 'polling']
  });
  window.socket = socket;

  socket.on('connect', function() {
    console.log('Projector connected to server');
    socket.emit('new-user', {
      screenName: customer.screenName || 'Projector',
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      isLoggedIn: !!customer.id,
      customerId: customer.id || null,
      bidderNumber: customer.bidderNumber || '',
      badge: customer.badge || '',
      profileImage: customer.profileImage || '',
      deviceInfo: typeof MHV !== 'undefined' && MHV.getDeviceInfo ? MHV.getDeviceInfo() : {}
    });
    // ⚠️ ASK for the current bid state (tweak 7). The projector only ever LISTENED
    // for live-bid-state pushes, so a screen opened (or reconnected, or refreshed)
    // mid-sale showed an empty bid panel until the NEXT bid landed — which on a
    // slow lot is a long time to be projecting nothing to a room. The server
    // answers this to the requesting socket alone.
    socket.emit('request-live-bid-state');
  });

  socket.on('disconnect', function() {
    console.log('Projector disconnected');
  });

  socket.on('heartbeat', function() {
    socket.emit('heartbeat-response');
  });

  // ---- Title display ----
  var titleEl = document.getElementById('projector-lot-title');
  var lotBadgeEl = document.getElementById('projector-lot-badge');
  var absenteeBadgeEl = document.getElementById('projector-absentee-badge'); // removed from DOM (null); legacy guards short-circuit
  var bidPanelEl = document.getElementById('projector-bid-panel');
  var pbpCurAmtEl = document.getElementById('pbp-current-amount');
  var pbpHolderEl = document.getElementById('pbp-holder');
  var pbpAskAmtEl = document.getElementById('pbp-ask-amount');
  // The current row is now two GRID CELLS (label+badge, amount), so hiding it means
  // hiding both — a single querySelector left the amount stranded in the grid.
  var pbpCurCells = bidPanelEl ? bidPanelEl.querySelectorAll('.pbp-current-cell') : [];

  function pbpMoney(n) { n = Number(n || 0); return '$' + (n % 1 === 0 ? String(n) : n.toFixed(2)); }

  // The lot the panel is currently showing, and whether it ever had numbers worth
  // showing. Both exist so a transient push cannot blank a panel a room full of
  // people is reading.
  var pbpProductId = null;
  var pbpHasContent = false;
  var liveBidCounts = {};   // productId -> bids, from live-bid-state (DB truth)
  var currentProductForBadge = null;

  // Render the current-bid + holder (ABSENTEE/FLOOR) + ask panel from live-bid-state.
  //
  // ⚠️ ONCE UP, IT STAYS UP for the lot on the block. The panel used to be hidden by
  // any state that happened to carry no numbers -- `(hasCur || ask > 0 || d.isOpen)`
  // is false for a perfectly ordinary push between bids -- so the current/ask display
  // appeared and then vanished a beat later, mid-sale, in front of the room. It now
  // comes down only when the lot actually CHANGES or is explicitly cleared
  // (productId null), which are the two cases where what it says would be wrong.
  function renderBidPanel(d) {
    // Remember what the ENGINE says about this lot. The absentee badge below used to
    // read a Shopify metafield, which only refreshes on a sync — so a lot whose
    // absentee bids arrived after that sync showed no badge to the room. Same fault
    // that hid late bids from the host page on 20260820.
    if (d && d.productId) {
      liveBidCounts[String(d.productId).replace(/[^0-9]/g, '')] = Number(d.numberOfBids) || 0;
      refreshAbsenteeBadgeFor(d.productId);
    }
    if (!bidPanelEl) return;
    if (!d || !d.productId) {
      bidPanelEl.style.display = 'none';
      pbpProductId = null; pbpHasContent = false;
      return;
    }
    // A different lot starts over: the previous lot's numbers must never linger.
    if (String(d.productId) !== String(pbpProductId)) {
      pbpProductId = String(d.productId);
      pbpHasContent = false;
    }
    var cur = Number(d.currentBid || 0);
    var ask = Number(d.ask || 0);
    var hasCur = cur > 0;
    // No "current" before any bid has landed.
    for (var ci = 0; ci < pbpCurCells.length; ci++) {
      pbpCurCells[ci].style.display = hasCur ? '' : 'none';
    }
    if (hasCur && pbpCurAmtEl) pbpCurAmtEl.textContent = pbpMoney(cur);
    if (pbpHolderEl) {
      if (hasCur && d.holder) {
        pbpHolderEl.textContent = d.holder === 'floor' ? 'FLOOR' : 'ABSENTEE';
        pbpHolderEl.className = 'pbp-holder pbp-holder--' + d.holder;
        pbpHolderEl.style.display = '';
      } else { pbpHolderEl.style.display = 'none'; pbpHolderEl.textContent = ''; }
    }
    if (pbpAskAmtEl) pbpAskAmtEl.textContent = ask > 0 ? pbpMoney(ask) : '—';
    if (hasCur || ask > 0 || d.isOpen) pbpHasContent = true;
    bidPanelEl.style.display = pbpHasContent ? '' : 'none';
  }

  function setTitle(text) {
    if (titleEl) titleEl.textContent = text || '\u00A0';
  }

  // Server sends metafields as an array: [{key, value, namespace}, ...]
  function getMetafield(product, key) {
    if (!product || !product.metafields) return '';
    // Handle array format (from getProductBySku)
    if (Array.isArray(product.metafields)) {
      for (var i = 0; i < product.metafields.length; i++) {
        if (product.metafields[i].key === key) return product.metafields[i].value || '';
      }
      return '';
    }
    // Handle flat object format (from collection cache)
    return product.metafields[key] || '';
  }

  function setLotBadge(product) {
    if (!lotBadgeEl) return;
    var lot = getMetafield(product, 'lot');
    if (!lot && product && product.title) {
      var m = product.title.match(/^Lot\s+(\S+)/i);
      if (m) lot = m[1];
    }
    if (lot) {
      lotBadgeEl.textContent = 'Lot ' + lot;
      lotBadgeEl.style.display = 'block';
    } else {
      lotBadgeEl.style.display = 'none';
    }
  }

  function setAbsenteeBadge(product) {
    if (!absenteeBadgeEl) return;
    // DB first, metafield only as the pre-connect fallback. The metafield lags a
    // sync, so a lot bid on after it was written showed the room no absentee badge.
    var pid = String((product && (product.productId || product.id)) || '').replace(/[^0-9]/g, '');
    currentProductForBadge = pid || null;
    var live = pid ? liveBidCounts[pid] : undefined;
    var bidCount = (live !== undefined)
      ? live
      : parseInt(getMetafield(product, 'absentee_number_of_bids') || 0, 10);
    absenteeBadgeEl.style.display = bidCount > 0 ? 'block' : 'none';
  }

  // A late live-bid-state for the lot on screen must be able to TURN THE BADGE ON —
  // the badge is painted once when the lot goes up, which is before the engine has
  // answered.
  function refreshAbsenteeBadgeFor(productId) {
    if (!absenteeBadgeEl || !productId) return;
    var pid = String(productId).replace(/[^0-9]/g, '');
    if (!currentProductForBadge || currentProductForBadge !== pid) return;
    var n = liveBidCounts[pid];
    if (n !== undefined) absenteeBadgeEl.style.display = n > 0 ? 'block' : 'none';
  }

  function clearBadges() {
    if (lotBadgeEl) lotBadgeEl.style.display = 'none';
    if (absenteeBadgeEl) absenteeBadgeEl.style.display = 'none';
    if (bidPanelEl) bidPanelEl.style.display = 'none';
    // Forget the lot too. Without this the panel's "stays up" memory would carry
    // across a clear, and the next push for a different lot would be treated as a
    // continuation of the one just taken down.
    pbpProductId = null; pbpHasContent = false;
  }

  // ---- Slide display (self-contained, no live-slideshow.js dependency) ----
  function showSlide(index) {
    var slideshow = document.getElementById('product-slideshow');
    if (!slideshow) return;
    var slides = slideshow.querySelectorAll('.slide');
    if (slides.length === 0) return;
    if (index < 0 || index >= slides.length) return;

    for (var i = 0; i < slides.length; i++) {
      slides[i].classList.toggle('active', i === index);
    }
    currentSlideIndex = index;
  }

  // ---- Slideshow display ----
  function showProjectorSlideshow(images, product) {
    var container = document.getElementById('product-slideshow-container');
    var slideshow = document.getElementById('product-slideshow');
    if (!container || !slideshow) return;

    if (!images || images.length === 0) {
      container.style.display = 'none';
      return;
    }

    // Set title and badges
    if (product && product.title) {
      setTitle(product.title);
    }
    currentProductHandle = (product && product.handle) || null;
    setLotBadge(product);
    setAbsenteeBadge(product);

    // Build slides
    var html = '';
    for (var i = 0; i < images.length; i++) {
      var alt = images[i].alt || ('Image ' + (i + 1));
      html += '<img src="' + MHV.escapeAttr(images[i].url) + '" alt="' + MHV.escapeAttr(alt) + '" class="slide" data-index="' + i + '">';
    }
    slideshow.innerHTML = html;

    currentImages = images;
    currentSlideIndex = 0;

    // Show container and first slide
    container.style.display = 'block';
    showSlide(0);
  }

  function clearProjectorSlideshow() {
    var container = document.getElementById('product-slideshow-container');
    var slideshow = document.getElementById('product-slideshow');
    if (container) container.style.display = 'none';
    if (slideshow) slideshow.innerHTML = '&nbsp;';
    currentImages = [];
    currentSlideIndex = 0;
    currentProductHandle = null;
    setTitle('\u00A0');
    clearBadges();
  }

  // ---- Multi-lot grid (One Money / Multi Money / On Choice) ----
  var currentGridLots = null;

  // Informative group banner: reflect the sale mode, e.g. "3 Lots × The Money",
  // "2 Lots — One Money", "4 Lots — Choice". Falls back to a plain count.
  function formatGroupLabel(count, saleMode) {
    var lots = count + (count === 1 ? ' Lot' : ' Lots');
    switch (saleMode) {
      case 'multi-money': return lots + ' × The Money';
      case 'one-money':   return lots + ' — One Money';
      case 'on-choice':   return lots + ' — Choice';
      default:            return 'Group of ' + lots;
    }
  }

  function renderProjectorGrid(lots, layout, hero, saleMode, chosenKeys, committedKeys) {
    var gridContainer = document.getElementById('product-grid-container');
    var slideshowContainer = document.getElementById('product-slideshow-container');
    if (!gridContainer) return;

    var cappedLots = Array.isArray(lots) ? lots.slice(0, 6) : [];
    var count = Math.max(1, Math.min(6, cappedLots.length));
    var mode = String(layout || count);
    var chosenSet = {}; (chosenKeys || []).forEach(function (k) { chosenSet[String(k)] = 1; });
    var committedSet = {}; (committedKeys || []).forEach(function (k) { committedSet[String(k)] = 1; });

    // Grid supersedes the single-product slideshow overlay
    if (slideshowContainer) slideshowContainer.style.display = 'none';

    gridContainer.className = 'product-grid-container projector-grid-container mode-grid-' + mode + (hero ? ' has-hero' : '');
    gridContainer.style.display = 'grid';

    var slots = gridContainer.querySelectorAll('.grid-slot');
    for (var s = 0; s < slots.length; s++) {
      var slot = slots[s];
      var slotIdx = parseInt(slot.getAttribute('data-slot'), 10) - 1;
      var img = slot.querySelector('.grid-slot-img');
      var title = slot.querySelector('.grid-slot-title');
      var lot = cappedLots[slotIdx];
      slot.classList.remove('empty');
      slot.classList.remove('blank');
      slot.classList.remove('chosen');
      slot.classList.remove('committed');
      if (!lot) {
        if ((mode === '3' && slot.dataset.slot === '4') || (mode === '5' && slot.dataset.slot === '6')) {
          slot.classList.add('blank');
        } else {
          slot.classList.add('empty');
        }
        if (img) { img.removeAttribute('src'); img.alt = ''; }
        if (title) title.textContent = '';
        continue;
      }
      // On Choice: green = picked by the winning bidder, gray = already sold.
      if (lot.choiceKey && committedSet[String(lot.choiceKey)]) slot.classList.add('committed');
      else if (lot.choiceKey && chosenSet[String(lot.choiceKey)]) slot.classList.add('chosen');
      var firstImage = (lot.product && Array.isArray(lot.product.images) && lot.product.images[0]) || null;
      var imgUrl = firstImage ? (firstImage.url || firstImage.src || '') : '';
      if (img) {
        if (imgUrl) {
          img.src = imgUrl.indexOf('?') >= 0 ? imgUrl + '&width=1400' : imgUrl + '?width=1400';
          img.alt = (lot.product && lot.product.title) || lot.sku || '';
        } else {
          img.removeAttribute('src');
          img.alt = '';
        }
      }
      if (title) {
        title.textContent = (lot.product && lot.product.title) || lot.sku || '';
      }
    }

    currentGridLots = cappedLots;
    // Projector title bar shows the sale-mode-aware group banner while a grid is active
    setTitle(formatGroupLabel(count, saleMode));
    clearBadges();
  }

  function clearProjectorGrid() {
    var gridContainer = document.getElementById('product-grid-container');
    var slideshowContainer = document.getElementById('product-slideshow-container');
    if (gridContainer) {
      gridContainer.style.display = 'none';
      gridContainer.className = 'product-grid-container projector-grid-container';
      var slots = gridContainer.querySelectorAll('.grid-slot');
      for (var i = 0; i < slots.length; i++) {
        var img = slots[i].querySelector('.grid-slot-img');
        var title = slots[i].querySelector('.grid-slot-title');
        if (img) { img.removeAttribute('src'); img.alt = ''; }
        if (title) title.textContent = '';
        slots[i].classList.remove('empty');
        slots[i].classList.remove('blank');
      }
    }
    if (slideshowContainer) slideshowContainer.style.display = '';
    currentGridLots = null;
  }

  // ---- Socket event handlers ----

  // Receive current lot (on connect + when host changes lot)
  socket.on('current-lot-updated', function(data) {
    if (!data) return;

    // Single-lot updates supersede any active multi-lot grid
    if (currentGridLots) clearProjectorGrid();

    // Handle clear
    if (data.sku === 'CLEARED' && data.action === 'clear') {
      clearProjectorSlideshow();
      return;
    }

    // Handle visibility toggles (ignore)
    if (data.action === 'toggle-slideshow' || data.action === 'visibility-toggle') {
      return;
    }

    // Handle lot with product
    if (data.product) {
      var images = [];
      if (data.product.images && data.product.images.length > 0) {
        for (var i = 0; i < data.product.images.length; i++) {
          var img = data.product.images[i];
          images.push({ url: img.src || img.url || img, alt: img.alt || '' });
        }
      }
      showProjectorSlideshow(images, data.product);
      // Ask for this lot's bid state rather than waiting for the next bid to land.
      // Same reason the connect handler asks: a lot can sit on the block for a long
      // time between bids, and the room should not be looking at an empty panel.
      socket.emit('request-live-bid-state');

      // Late joiner: jump to current slide
      if (typeof data.slideIndex === 'number' && data.slideIndex > 0) {
        showSlide(data.slideIndex);
      }
    }
  });

  // Multi-lot grid broadcast (One Money / Multi Money / On Choice)
  socket.on('current-lot-grid-updated', function(data) {
    // A multi-lot grid has no single current bid, so the panel comes down and
    // forgets its lot along with it.
    if (bidPanelEl) bidPanelEl.style.display = 'none';
    pbpProductId = null; pbpHasContent = false;
    if (!data || data.mode === 'clear' || !Array.isArray(data.lots) || data.lots.length === 0) {
      clearProjectorGrid();
      return;
    }
    try { renderProjectorGrid(data.lots, data.layout, data.hero, data.saleMode, data.chosenKeys, data.committedKeys); }
    catch (e) { console.error('projector grid render error:', e); }
  });

  // Sync slides from host owner
  socket.on('slideshow-slide-changed', function(data) {
    if (!data || currentImages.length === 0) return;
    showSlide(data.slideIndex);
  });

  // Show absentee badge when a bid comes in for the current lot
  socket.on('absentee-bid-activity', function(data) {
    if (!data || data.type !== 'bid' || !absenteeBadgeEl) return;
    // Match by product handle if available
    if (currentProductHandle && data.productHandle === currentProductHandle) {
      absenteeBadgeEl.style.display = 'block';
    }
  });

  // Clear absentee badge when host clicks OUT
  socket.on('absentee-badge-clear', function() {
    if (absenteeBadgeEl) absenteeBadgeEl.style.display = 'none';
  });

  // Current bid + holder (ABSENTEE/FLOOR) + ask — driven by the host live-bid state.
  socket.on('live-bid-state', renderBidPanel);

  // ---- Move play/mute buttons into floating controls ----
  var controlsPanel = document.getElementById('projector-controls');
  var playBtn = document.getElementById('play-pause-btn');
  var muteBtn = document.getElementById('mute-toggle-btn');
  var fsBtn = document.getElementById('projector-fullscreen-btn');

  if (controlsPanel && fsBtn) {
    // Find the button row (second row)
    var btnRow = controlsPanel.querySelectorAll('.projector-controls-row')[1];
    if (btnRow) {
      if (playBtn) btnRow.insertBefore(playBtn, fsBtn);
      if (muteBtn) btnRow.insertBefore(muteBtn, fsBtn);
    }
  }

  // ---- Text size slider (affects title + lot badge) ----
  var sizeSlider = document.getElementById('projector-size-slider');

  function applyTextSize(size) {
    if (titleEl) titleEl.style.fontSize = size + 'px';
    if (lotBadgeEl) lotBadgeEl.style.fontSize = Math.round(size * 0.9) + 'px';
    // The bid panel scales too (tweak 8). Every size inside it is in `em`, so setting
    // ONE font-size here moves the label, the amounts and the FLOOR/ABSENTEE badge
    // together. The slider used to move only the title and lot badge, so the bid
    // figures — the numbers the room is actually reading — stayed fixed while
    // everything around them grew. 0.48 keeps the default 42 -> the original 20px.
    if (bidPanelEl) bidPanelEl.style.fontSize = Math.max(10, Math.round(size * 0.48)) + 'px';
  }

  var savedSize = localStorage.getItem('projectorTitleSize');
  if (savedSize) {
    var size = parseInt(savedSize, 10);
    applyTextSize(size);
    if (sizeSlider) sizeSlider.value = size;
  }
  if (sizeSlider) {
    sizeSlider.addEventListener('input', function() {
      var size = parseInt(sizeSlider.value, 10);
      applyTextSize(size);
      localStorage.setItem('projectorTitleSize', size);
    });
  }

  // ---- Auto-hide controls after inactivity ----
  var hideTimeout = null;
  var HIDE_DELAY = 4000; // 4 seconds of inactivity

  function showControls() {
    if (controlsPanel) {
      controlsPanel.classList.remove('auto-hidden');
    }
    clearTimeout(hideTimeout);
    hideTimeout = setTimeout(hideControls, HIDE_DELAY);
  }

  function hideControls() {
    if (controlsPanel) {
      controlsPanel.classList.add('auto-hidden');
    }
  }

  // Show on mouse move anywhere on page
  document.addEventListener('mousemove', showControls);
  // Show on touch
  document.addEventListener('touchstart', showControls);
  // Keep visible while hovering over the controls
  if (controlsPanel) {
    controlsPanel.addEventListener('mouseenter', function() {
      clearTimeout(hideTimeout);
      controlsPanel.classList.remove('auto-hidden');
    });
    controlsPanel.addEventListener('mouseleave', function() {
      hideTimeout = setTimeout(hideControls, HIDE_DELAY);
    });
  }

  // Start the auto-hide timer
  hideTimeout = setTimeout(hideControls, HIDE_DELAY);

  // ---- Disable auto-unmute on click (projector should stay muted until manually toggled) ----
  window._projectorPage = true;

  // ---- Fullscreen toggle ----
  var projectorContainer = document.getElementById('projector-container');

  function toggleFullscreen() {
    if (!projectorContainer) return;
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (projectorContainer.requestFullscreen) {
        projectorContainer.requestFullscreen();
      } else if (projectorContainer.webkitRequestFullscreen) {
        projectorContainer.webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  }

  // Keyboard shortcut: F (or Ctrl/Cmd+F) toggles fullscreen; Esc exits is native.
  // Ignored while typing in a field (the size slider is the only input here).
  document.addEventListener('keydown', function(e) {
    if (e.key !== 'f' && e.key !== 'F') return;
    var t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    // Ctrl/Cmd+F would open the browser's find bar — suppress it on the kiosk.
    if (e.ctrlKey || e.metaKey) e.preventDefault();
    if (e.altKey || e.shiftKey) return;
    toggleFullscreen();
    if (typeof showControls === 'function') showControls();
  });

  if (fsBtn && projectorContainer) {
    fsBtn.addEventListener('click', toggleFullscreen);

    // Update icon on fullscreen change
    function onFsChange() {
      var isFs = !!document.fullscreenElement || !!document.webkitFullscreenElement;
      var svg = fsBtn.querySelector('svg path');
      if (svg) {
        if (isFs) {
          svg.setAttribute('d', 'M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z');
        } else {
          svg.setAttribute('d', 'M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z');
        }
      }
    }
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
  }

})();
